import { InverseHeader } from '@jouzen/ecom-components/layout';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Tabs } from '@jouzen/ecom-components/v3/Tabs';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import BackdropBlur from '@/app/components/BackdropBlur';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import MobileTabs from './MobileTabs';

const AURORA_VARIANTS = {
  0: { height: 745 },
  1: { height: 606 },
  2: { height: 600 },
};

interface AppImage {
  alt: string;
  src: string;
}

const WhyOuraPersonalInsights = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const t = useTranslations();

  const appUiImages: Record<number, AppImage> = {
    0: {
      alt: 'june_app_image_alt',
      src: 'june_app_ui_2025',
    },
    1: {
      alt: 'july_app_image_alt',
      src: 'july_app_ui_2025',
    },
    2: {
      alt: 'august_app_image_alt',
      src: 'aug_app_ui_2025',
    },
  };
  const appImage = appUiImages[activeTab]!;

  return (
    <section
      className="bg-sandstone-200 py-16 text-sandstone-500 md:pb-0"
      data-cy="why-oura-personal-insights"
    >
      <LayoutGrid>
        <GridItem
          className="lg:pb-16"
          colEnd={{ sm: 'main', lg: 12 }}
          colStart={{ sm: 'main', lg: 'main' }}
        >
          <SectionHeading
            animated
            content="insights_title"
            emAnimationProps={{
              transition: { duration: 2, type: 'tween', delay: 0.5 },
            }}
            indentation="pl-14 lg:pl-36"
            multiline
            typographyProps={{
              className: 'leading-heading tracking-tighter',
              color: 'inherit',
              Element: 'h3',
              variant: 'h3',
            }}
            wrapperAnimationProps={{
              transition: { duration: 1.25, type: 'tween', delay: 0.25 },
            }}
          />
        </GridItem>
        <GridItem
          className="pt-6 pb-12 lg:pb-0"
          colEnd={{ sm: 'main', lg: 'main' }}
          colStart={{ sm: 'main', lg: 15 }}
        >
          <Motion
            initial={{ filter: 'blur(5px)', opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <Typography color="inherit">{t('insights_copy')}</Typography>
          </Motion>
        </GridItem>
        <InverseHeader className="col-main flex justify-center md:hidden" enter>
          <MobileTabs />
        </InverseHeader>
        <InverseHeader
          className="col-full hidden justify-center overflow-hidden md:relative md:flex md:h-[900px]"
          enter
        >
          <Motion
            className="absolute bottom-28 z-10"
            data-cy="personal-insights-app-ui"
            initial={{ height: 745 }}
            animate={AURORA_VARIANTS[activeTab as keyof typeof AURORA_VARIANTS]}
          >
            <div className="relative flex h-full w-[361px] items-center rounded-lg">
              <BackdropBlur
                color={{ sm: 'dark/10', lg: 'dark/10' }}
                variant="card"
              />
              <Image
                alt={t(appImage.alt)}
                className="relative max-h-[745px] w-full rounded-md"
                data-cy="personal-insights-app-ui-image-desktop"
                format="png"
                src={t(appImage.src)}
                width={361}
                height={745}
                priority
              />
            </div>
          </Motion>
          <Tabs
            inside
            inverted
            onChange={(index) => setActiveTab(index)}
            orientation="reverse"
            style="pill"
            tabs={[
              {
                label: t('june'),
              },
              {
                label: t('july'),
              },
              {
                label: t('august'),
              },
            ]}
            tabPanels={[
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      localizedAlt="home_image_scroller_image_1_alt"
                      fill
                      priority
                      className="object-cover"
                      src="why-oura/oreo/june"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      localizedAlt='home_image_scroller_image_3_alt'
                      fill
                      className="object-cover"
                      src="why-oura/oreo/july"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      localizedAlt='women_sitting_app_alt'
                      fill
                      className="object-cover"
                      src="why-oura/oreo/august"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
            ]}
          />
        </InverseHeader>
      </LayoutGrid>
    </section>
  );
};

export default WhyOuraPersonalInsights;
